if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');
        this.notifyButton = this.querySelector('[data-notify-open]');
        this.notifyModal = this.querySelector('[data-notify-modal]');
        this.notifyForm = this.querySelector('[data-notify-form]');
        this.notifySuccess = this.querySelector('[data-notify-success]');
        this.notifyError = this.querySelector('[data-notify-error]');
        this.notifyVariantIdInput = this.querySelector('[data-notify-variant-id]');
        this.notifyVariantTitleInput = this.querySelector('[data-notify-variant-title]');
        this.notifyVariantDisplay = this.querySelector('[data-notify-variant-display]');
        this.notifyVariantSelect = this.querySelector('[data-notify-variant-select]');
        this.notifyDropdown = this.querySelector('[data-notify-dropdown]');
        this.notifyDropdownTrigger = this.querySelector('[data-notify-dropdown-trigger]');
        this.notifyDropdownMenu = this.querySelector('[data-notify-dropdown-menu]');
        this.notifyDropdownLabel = this.querySelector('[data-notify-dropdown-label]');
        this.notifySubmitButton = this.querySelector('.notify-me-submit');
        this.notifyEnabled = this.dataset.notifyEnabled !== 'false';
        this.variantChangeUnsubscriber = null;
        this.onDocumentClick = (event) => {
          if (!this.notifyDropdown?.contains(event.target)) {
            this.closeNotifyDropdown();
          }
        };
        this.onNotifyEsc = (event) => {
          if (event.key === 'Escape') {
            this.closeNotifyDropdown();
            this.closeNotifyModal();
          }
        };

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.bindNotifyEvents();
      }

      connectedCallback() {
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (!data) return;
          if (String(data.sectionId) !== String(this.dataset.sectionId)) return;
          this.handleVariantChange(data.variant);
        });
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
        document.removeEventListener('keydown', this.onNotifyEsc);
        document.removeEventListener('click', this.onDocumentClick);
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      bindNotifyEvents() {
        if (this.notifyButton) {
          this.notifyButton.addEventListener('click', () => this.openNotifyModal());
        }

        if (this.notifyModal) {
          this.notifyModal.querySelectorAll('[data-notify-close]').forEach((closer) => {
            closer.addEventListener('click', () => this.closeNotifyModal());
          });
        }

        if (this.notifyForm) {
          this.notifyForm.addEventListener('submit', this.submitNotifyForm.bind(this));
        }

        if (this.notifyVariantSelect) {
          this.buildNotifyDropdownFromSelect();
          this.notifyVariantSelect.addEventListener('change', () => {
            this.syncNotifyVariantFromSelect();
          });
          this.syncNotifyVariantFromSelect();
        }

        if (this.notifyDropdownTrigger) {
          this.notifyDropdownTrigger.addEventListener('click', () => {
            if (this.notifyDropdown?.classList.contains('is-open')) {
              this.closeNotifyDropdown();
            } else {
              this.openNotifyDropdown();
            }
          });
        }
      }

      handleVariantChange(variant) {
        if (!variant) {
          this.setNotifyState(true);
          if (this.notifyVariantIdInput) this.notifyVariantIdInput.value = '';
          if (this.notifyVariantTitleInput) this.notifyVariantTitleInput.value = '';
          return;
        }

        const isSoldOut = variant.available === false;
        this.setNotifyState(isSoldOut);

        if (this.notifyVariantIdInput) {
          this.notifyVariantIdInput.value = variant.id || '';
        }
        if (this.notifyVariantTitleInput) {
          this.notifyVariantTitleInput.value = variant.title || '';
        }
        if (this.notifyVariantDisplay) {
          this.notifyVariantDisplay.textContent = variant.title || '';
        }
        if (this.notifyVariantSelect && variant?.id) {
          const hasVariantOption = Array.from(this.notifyVariantSelect.options).some(
            (option) => option.value === String(variant.id)
          );
          if (hasVariantOption) {
            this.notifyVariantSelect.value = String(variant.id);
          }
          this.syncNotifyVariantFromSelect();
        }
      }

      setNotifyState(showNotify) {
        if (!this.submitButton) return;
        if (!this.notifyEnabled || !this.notifyButton) {
          this.classList.remove('notify-active');
          this.submitButton.classList.remove('hidden');
          return;
        }

        this.classList.toggle('notify-active', showNotify);
        this.notifyButton.classList.toggle('hidden', !showNotify);
        this.submitButton.classList.toggle('hidden', showNotify);
      }

      openNotifyModal() {
        if (!this.notifyModal) return;
        if (!this.notifyModal.hasAttribute('data-moved-to-body')) {
          document.body.appendChild(this.notifyModal);
          this.notifyModal.setAttribute('data-moved-to-body', 'true');
        }
        this.notifySuccess?.classList.add('hidden');
        this.notifyError?.classList.add('hidden');
        this.closeNotifyDropdown();
        this.notifyModal.classList.remove('hidden');
        this.notifyModal.setAttribute('aria-hidden', 'false');
        document.body.classList.add('notify-modal-open');
        document.addEventListener('keydown', this.onNotifyEsc);
        document.addEventListener('click', this.onDocumentClick);
      }

      closeNotifyModal() {
        if (!this.notifyModal) return;
        this.closeNotifyDropdown();
        this.notifyModal.classList.add('hidden');
        this.notifyModal.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('notify-modal-open');
        document.removeEventListener('keydown', this.onNotifyEsc);
        document.removeEventListener('click', this.onDocumentClick);
      }

      submitNotifyForm(event) {
        event.preventDefault();
      
        this.notifySuccess?.classList.add('hidden');
        this.notifyError?.classList.add('hidden');
      
        const formData = new FormData(this.notifyForm);
        const email = (formData.get('email') || '').toString().trim();
        const name = (formData.get('name') || '').toString().trim();
        const variantId = (this.notifyVariantIdInput?.value || '').trim();
      
        if (!email || !variantId) {
          this.showNotifyError('Please select a variant and enter your email address.');
          return;
        }
      
        if (this.notifySubmitButton) {
          this.notifySubmitButton.setAttribute('disabled', 'disabled');
          this.notifySubmitButton.textContent = 'Submitting...';
        }
      
        try {
          const publicKey = this.dataset.klaviyoPublicKey || 'SpRNGB';
      
          window._learnq = window._learnq || [];
      
          // Identify the user first (so name is captured in Klaviyo profile)
          if (name) {
            window._learnq.push(['identify', {
              '$email': email,
              '$first_name': name.split(' ')[0] || name,
              '$last_name': name.split(' ').slice(1).join(' ') || '',
            }]);
          }
      
          // Back-in-stock subscription with correct Klaviyo payload
          window._learnq.push(['backInStock', {
            '$email': email,
            'variant': Number(variantId) || variantId,
            'platform': 'shopify',
            'item_id': variantId,
          }]);
      
          setTimeout(() => {
            this.notifySuccess?.classList.remove('hidden');
          }, 800);
      
          this.notifyForm.reset();
          this.syncNotifyVariantFromSelect();
      
        } catch (error) {
          this.showNotifyError('Unable to submit right now. Please try again.');
          console.error(error);
        } finally {
          if (this.notifySubmitButton) {
            this.notifySubmitButton.removeAttribute('disabled');
            this.notifySubmitButton.textContent = 'Email me when available';
          }
        }
      }

      showNotifyError(message) {
        if (!this.notifyError) return;
        this.notifyError.textContent = message;
        this.notifyError.classList.remove('hidden');
      }

      syncNotifyVariantFromSelect() {
        if (!this.notifyVariantSelect) return;
        const selectedOption = this.notifyVariantSelect.options[this.notifyVariantSelect.selectedIndex];
        if (!selectedOption) return;
        if (this.notifyVariantIdInput) this.notifyVariantIdInput.value = selectedOption.value || '';
        if (this.notifyVariantTitleInput) {
          this.notifyVariantTitleInput.value = selectedOption.dataset.variantTitle || selectedOption.textContent.trim();
        }
        if (this.notifyDropdownLabel) {
          this.notifyDropdownLabel.textContent = selectedOption.textContent.trim();
        }
        if (this.notifyDropdownMenu) {
          this.notifyDropdownMenu.querySelectorAll('[data-notify-dropdown-option]').forEach((button) => {
            const isSelected = button.dataset.value === selectedOption.value;
            button.classList.toggle('is-selected', isSelected);
            button.setAttribute('aria-selected', isSelected ? 'true' : 'false');
          });
        }
      }

      buildNotifyDropdownFromSelect() {
        if (!this.notifyVariantSelect || !this.notifyDropdownMenu) return;
        this.notifyDropdownMenu.innerHTML = '';
        Array.from(this.notifyVariantSelect.options).forEach((option) => {
          const item = document.createElement('button');
          item.type = 'button';
          item.className = 'notify-me-dropdown__option';
          item.dataset.notifyDropdownOption = 'true';
          item.dataset.value = option.value;
          item.dataset.variantTitle = option.dataset.variantTitle || option.textContent.trim();
          item.setAttribute('role', 'option');
          item.setAttribute('aria-selected', option.selected ? 'true' : 'false');
          item.textContent = option.textContent.trim();
          if (option.selected) item.classList.add('is-selected');
          item.addEventListener('click', () => {
            this.notifyVariantSelect.value = option.value;
            this.notifyVariantSelect.dispatchEvent(new Event('change', { bubbles: true }));
            this.closeNotifyDropdown();
          });
          this.notifyDropdownMenu.appendChild(item);
        });
      }

      openNotifyDropdown() {
        if (!this.notifyDropdown || !this.notifyDropdownTrigger || !this.notifyDropdownMenu) return;
        this.notifyDropdown.classList.add('is-open');
        this.notifyDropdownTrigger.setAttribute('aria-expanded', 'true');
        this.notifyDropdownMenu.classList.remove('hidden');
      }

      closeNotifyDropdown() {
        if (!this.notifyDropdown || !this.notifyDropdownTrigger || !this.notifyDropdownMenu) return;
        this.notifyDropdown.classList.remove('is-open');
        this.notifyDropdownTrigger.setAttribute('aria-expanded', 'false');
        this.notifyDropdownMenu.classList.add('hidden');
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
